// @flow
import type { Technology } from './technology';

import type { AutoPopulateOrdersModeSetting } from 'modules/auto-prepopulate-orders';
import type { TagTree } from 'modules/tag-tree';
import { toDate } from 'modules/date';
import { type Asset } from 'modules/asset';
import { automaticShipmentsAreEnabledByDefault } from 'modules/automatic-shipments';
import type { Params } from 'modules/automatic-shipments';

export type TechnologySpecification = {
  icon: string,
  dark_icon: string,
  page: string,
};

export type CatalogPermissionSpec = $ReadOnlyArray<string>;
export type CatalogPermissionHash = { [key: string]: CatalogPermissionSpec };

export type CatalogDates = {
  default_cancel_days?: string,
  first_live?: string,
  first_ship?: string | null,
  last_live?: string,
  last_ship?: string,
  ship_min_days?: number | string,
  ship_max_days?: number,
  ship_default_days?: number | string,
  [shipWindow: string]: string,
};

export type CatalogType =
  | 'nonblocking'
  | 'blocking'
  | 'backorder'
  | 'unrestricted';

export type ResolvedCatalogAtsType = CatalogType | 'shipmentsByAvailableStock';

type minOrderAmountForCurrency = { +[currency_code: string]: number };
type maxOrderAmountForCurrency = { +[currency_code: string]: number };

export type PrintedCatalog = $ReadOnly<{|
  printed_at: ?string,
  division: ?string,
  url: ?string,
|}>;

export type Catalog = $ReadOnly<{|
  key: string,
  name: string,
  technologies: { [name: string]: TechnologySpecification },
  archived: boolean,
  dates: CatalogDates,
  _id: string,
  permissions: string[],
  position: number | null,
  type: CatalogType,
  assets?: $ReadOnly<{
    catalog_tiles?: $ReadOnlyArray<Asset>,
    catalog_pages?: $ReadOnlyArray<Asset>,
    category_pages?: $ReadOnlyArray<Asset>,
  }>,
  classification?: 'ATS' | 'Prebook',
  client_fields: $ReadOnly<{
    selling_groups_with_number?: $ReadOnlyArray<string>,
    [string]: mixed,
  }>,
  ats_label_max: ?number,
  ats_label_max_rep?: ?number,
  allow_dropship: boolean,
  auto_prepopulate_orders_mode?: AutoPopulateOrdersModeSetting,
  min_order_amount?: number,
  max_order_amount?: number,
  min_order_amount_for_currency?: minOrderAmountForCurrency,
  max_order_amount_for_currency?: maxOrderAmountForCurrency,
  // visible_to determines the permissions for a catalog
  // all means that all types of users can access the catalog
  // rep limits the permissions to only the reps.
  visible_to?: 'all' | 'rep' | 'dealer',
  tags?: TagTree,
  elastic_instance_url: string | null,
  is_openable: boolean,
  dropship: boolean,
  printed_catalog: null | PrintedCatalog,
  printed_catalogs: $ReadOnlyArray<PrintedCatalog>,
  state: 'published' | 'completed' | 'draft' | 'publishing',
  total_copies?: number,
|}>;

export const maxOrderAmount = (
  catalog: Catalog,
  currencyCode: string,
): number => {
  const maxByCurrency = catalog.max_order_amount_for_currency || {};

  if (currencyCode in maxByCurrency) {
    return maxByCurrency[currencyCode];
  }

  if (typeof catalog.max_order_amount === 'number')
    return catalog.max_order_amount;
  return Infinity;
};
export const minOrderAmount = (
  catalog: Catalog,
  currencyCode: string,
): number => {
  const minByCurrency = catalog.min_order_amount_for_currency || {};

  if (currencyCode in minByCurrency) {
    return minByCurrency[currencyCode];
  }

  if (typeof catalog.min_order_amount === 'number')
    return catalog.min_order_amount;
  return -Infinity;
};

export const byKey = (haystack: Catalog[], needle: string): Catalog | null =>
  haystack.find(({ key }) => key === needle) || null;

export const technologies = (catalog: Catalog): Technology[] =>
  Object.entries(catalog.technologies)
    // eslint-disable-next-line camelcase
    .map(([name, { icon, dark_icon, page }]: [string, any]) => ({
      name,
      lightIcon: icon,
      darkIcon: dark_icon,
      detailImage: page,
    }));

export const hasShipWindow = (dates: CatalogDates): boolean =>
  Object.entries(dates).some(([k]) => k.match(/^ship_window_\d+$/));

export const printedCatalogUpdatedAt = (
  printCatalog: PrintedCatalog | null,
): Date | null =>
  printCatalog ? toDate(printCatalog.printed_at || null) : null;

export const automaticShipmentsAreAvailableForCatalog = (
  params: Params,
): boolean =>
  automaticShipmentsAreEnabledByDefault(params) &&
  params.catalog.dates &&
  hasShipWindow(params.catalog.dates);
